import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "../../lib/api";
import { apiError } from "../../context/AuthContext";
import { UploadButton } from "./ImageUpload";
import { LOGO } from "../../lib/api";
import { CATS, HERO } from "../../lib/categories";
import { useAdminList, AdminHead, Field, TextInput, Toggle, PrimaryBtn } from "./adminUi";

const SOCIAL = ["instagram", "facebook", "tiktok", "pinterest"];
const Card = ({ title, children }) => <div className="rounded-3xl bg-white shadow-sm p-6 space-y-4"><h3 className="font-bold text-navy">{title}</h3>{children}</div>;

export default function AdminSettings() {
  const { data } = useAdminList("/admin/settings");
  const [s, setS] = useState(null);
  useEffect(() => {
    if (data) setS(data);
  }, [data]);
  if (!s) return <div className="h-64 rounded-3xl bg-white animate-pulse" />;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      setS((await http.put("/admin/settings", { ...s, free_shipping_threshold: Number(s.free_shipping_threshold), flat_shipping: Number(s.flat_shipping) })).data);
      toast.success("Settings saved — refresh the storefront to see changes");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <form onSubmit={save} data-testid="admin-settings">
      <AdminHead title="Marketing & settings"><PrimaryBtn data-testid="settings-save-button">Save settings</PrimaryBtn></AdminHead>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Logo">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <img src={s.logo_url || LOGO} alt="Current logo" className="h-24 w-auto rounded-xl bg-navy p-2" data-testid="settings-logo-preview" />
            <div className="space-y-2">
              <UploadButton kind="logo" label="Upload new logo" onUploaded={(url) => setS((cur) => ({ ...cur, logo_url: url }))} testid="settings-logo-upload" />
              {s.logo_url && <button type="button" onClick={() => setS({ ...s, logo_url: null })} className="text-sm text-tpink font-semibold" data-testid="settings-logo-reset">Use default logo</button>}
              <p className="text-xs text-navy/50">PNG with transparent background recommended. Click Save settings to apply everywhere (site & emails).</p>
            </div>
          </div>
        </Card>
        <Card title="Homepage hero image">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <img src={s.hero_image || HERO} alt="Current hero" className="w-40 h-24 rounded-xl object-cover bg-sand" data-testid="settings-hero-preview" />
            <div className="space-y-2">
              <UploadButton kind="hero" label="Upload hero image" onUploaded={(url) => setS((cur) => ({ ...cur, hero_image: url }))} testid="settings-hero-upload" />
              {s.hero_image && <button type="button" onClick={() => setS({ ...s, hero_image: null })} className="text-sm text-tpink font-semibold" data-testid="settings-hero-reset">Use default image</button>}
              <p className="text-xs text-navy/50">Wide landscape photo (1920×1080+) works best. It's the homepage banner image. Click Save settings to apply.</p>
            </div>
          </div>
        </Card>
        <Card title="Shop by Category images">
          <div className="grid sm:grid-cols-2 gap-4" data-testid="settings-category-images">
            {CATS.map(([name, , def]) => {
              const cur = s.category_images?.[name];
              const slug = name.toLowerCase();
              const setImg = (url) => setS((c) => ({ ...c, category_images: { ...c.category_images, [name]: url } }));
              return (
                <div key={name} className="flex items-center gap-3">
                  <img src={cur || def} alt={`${name} category`} className="w-20 h-20 rounded-2xl object-cover bg-sand" data-testid={`settings-category-preview-${slug}`} />
                  <div className="space-y-1.5">
                    <p className="font-semibold text-navy text-sm">{name}</p>
                    <UploadButton kind="category" label="Change" onUploaded={setImg} testid={`settings-category-upload-${slug}`} />
                    {cur && <button type="button" onClick={() => setImg(null)} className="text-xs text-tpink font-semibold" data-testid={`settings-category-reset-${slug}`}>Use default</button>}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-navy/50">Landscape or square photos work best. Click Save settings to apply.</p>
        </Card>
        <Card title="Announcement bar">
          <Field label="Message"><TextInput maxLength={200} value={s.announcement} onChange={set("announcement")} data-testid="settings-announcement-input" /></Field>
          <Toggle label="Show announcement" checked={s.announcement_enabled} onChange={(v) => setS({ ...s, announcement_enabled: v })} testid="settings-announcement-toggle" />
        </Card>
        <Card title="Social profiles">
          {SOCIAL.map((k) => (
            <Field key={k} label={k}><TextInput type="url" placeholder={`https://www.${k}.com/...`} value={s.social?.[k] || ""} onChange={(e) => setS({ ...s, social: { ...s.social, [k]: e.target.value } })} data-testid={`settings-social-${k}`} /></Field>
          ))}
        </Card>
        <Card title="Shipping">
          <Field label="Free U.S. shipping threshold ($)"><TextInput type="number" step="0.01" value={s.free_shipping_threshold} onChange={set("free_shipping_threshold")} data-testid="settings-free-threshold" /></Field>
          <Field label="Flat U.S. shipping rate ($)"><TextInput type="number" step="0.01" value={s.flat_shipping} onChange={set("flat_shipping")} data-testid="settings-flat-shipping" /></Field>
        </Card>
        <Card title="Analytics (loaded only after cookie consent — Phase 4)">
          <Field label="Google Analytics 4 ID" hint="Format G-XXXXXXX"><TextInput value={s.ga4_id} onChange={set("ga4_id")} data-testid="settings-ga4" /></Field>
          <Field label="Meta Pixel ID" hint="Numbers only"><TextInput value={s.meta_pixel_id} onChange={set("meta_pixel_id")} data-testid="settings-pixel" /></Field>
        </Card>
      </div>
    </form>
  );
}
